import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, LoginCredentials, TokenResponse, FeatureFlags } from '../types/api';
import { apiClient, apiClientFormEncoded } from '../services/api/client';
import { identifyUser, resetAnalytics } from '../lib/analytics';
import searchService from '../services/search/searchService';

/**
 * Active super-admin impersonation session, persisted to localStorage so it
 * survives reloads. Cleared on logout, on /admin/impersonate/stop success,
 * or after the hard-stop timeout (30 min) elapses.
 */
export interface ImpersonationState {
  id: string;
  name: string;
  code: string;
  startedAt: number; // epoch ms
  reason: string;
}

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiry: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  impersonatingOrg: ImpersonationState | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  checkTokenExpiry: () => Promise<void>;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  setImpersonating: (state: ImpersonationState | null) => void;
  clearAuth: () => void;
  clearError: () => void;
}

// Token storage helpers
const TOKEN_KEY = 'sprout_access_token';
const REFRESH_TOKEN_KEY = 'sprout_refresh_token';
const _USER_KEY = 'sprout_user';

const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const setStoredToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store token:', error);
  }
};

const removeStoredToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove token:', error);
  }
};

const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

const setStoredRefreshToken = (token: string): void => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store refresh token:', error);
  }
};

const removeStoredRefreshToken = (): void => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove refresh token:', error);
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: getStoredToken(),
      refreshToken: getStoredRefreshToken(),
      tokenExpiry: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      impersonatingOrg: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        
        try {
          // FastAPI OAuth2 expects form-encoded data
          const formData = new URLSearchParams();
          formData.append('username', credentials.username);
          formData.append('password', credentials.password);
          
          console.log('Attempting login to:', apiClientFormEncoded.defaults.baseURL + '/auth/login');
          console.log('Credentials:', { username: credentials.username });
          
          const response = await apiClientFormEncoded.post<TokenResponse>(
            '/auth/login',
            formData
          );

          const { access_token, refresh_token } = response.data;

          // Store tokens first so the apiClient interceptor can use them
          setStoredToken(access_token);
          if (refresh_token) {
            setStoredRefreshToken(refresh_token);
          }

          // Set tokens in state so apiClient interceptor picks them up
          set({ accessToken: access_token, refreshToken: refresh_token || null });

          // Fetch user profile using the new token
          const userResponse = await apiClient.get<User>('/users/me');
          const user = userResponse.data;

          // Calculate token expiry (assuming 30 minute expiry)
          const tokenExpiry = Date.now() + 30 * 60 * 1000;

          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token || null,
            tokenExpiry,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Identify user for analytics (no-op if PostHog is not configured)
          identifyUser(user.id, {
            email: user.email,
            org_id: user.organization_id,
            role: user.roles?.[0],
            is_staff: user.is_staff,
            user_type: 'staff',
          });
        } catch (error: any) {
          console.error('Login error:', error);
          console.error('Error response:', error.response);
          console.error('Error details:', error.response?.data);
          
          set({
            isLoading: false,
            error: error.response?.data?.detail || 'Login failed',
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          // Call logout endpoint (optional, mainly for server-side cleanup)
          await apiClientFormEncoded.post('/auth/logout');
        } catch (error) {
          // Ignore logout errors
          console.error('Logout error:', error);
        } finally {
          // Clear local state regardless
          removeStoredToken();
          removeStoredRefreshToken();
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            tokenExpiry: null,
            isAuthenticated: false,
            error: null,
          });
          // Reset analytics identity so subsequent events aren't tied to this user
          resetAnalytics();
          // POPIA s19/MEDIUM-4 (audit 2026-04-29): drop persisted search
          // history so child names typed into the search box don't
          // outlive the session on a shared device.
          searchService.clearAllData();
        }
      },

      refreshAccessToken: async () => {
        const currentRefreshToken = get().refreshToken;
        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }
        
        try {
          const response = await apiClientFormEncoded.post<TokenResponse>(
            '/auth/refresh',
            { refresh_token: currentRefreshToken }
          );
          
          const { access_token, refresh_token, user } = response.data;
          
          // Update tokens and user
          setStoredToken(access_token);
          if (refresh_token) {
            setStoredRefreshToken(refresh_token);
          }
          
          // Calculate token expiry (assuming 30 minute expiry)
          const tokenExpiry = Date.now() + 30 * 60 * 1000;
          
          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token || currentRefreshToken,
            tokenExpiry,
            isAuthenticated: true,
            error: null,
          });
        } catch (error) {
          // Refresh failed, clear auth
          get().clearAuth();
          throw error;
        }
      },

      checkTokenExpiry: async () => {
        const state = get();
        if (!state.tokenExpiry || !state.refreshToken) {
          return;
        }
        
        // Check if token expires in less than 5 minutes
        const now = Date.now();
        const expiresIn = state.tokenExpiry - now;
        
        if (expiresIn < 5 * 60 * 1000) { // 5 minutes
          try {
            await get().refreshAccessToken();
          } catch (error) {
            // Silent fail, will be handled by interceptor
            console.error('Token refresh failed:', error);
          }
        }
      },

      setUser: (user: User) => {
        set({ user });
      },

      setToken: (token: string) => {
        setStoredToken(token);
        set({ accessToken: token, isAuthenticated: true });
      },

      setRefreshToken: (token: string) => {
        setStoredRefreshToken(token);
        set({ refreshToken: token });
      },

      setImpersonating: (state: ImpersonationState | null) => {
        set({ impersonatingOrg: state });
      },

      clearAuth: () => {
        removeStoredToken();
        removeStoredRefreshToken();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          tokenExpiry: null,
          isAuthenticated: false,
          error: null,
          // Logging out always ends an impersonation session.
          impersonatingOrg: null,
        });
        resetAnalytics();
        // Mirror the cleanup in `logout` so a token-refresh failure that
        // routes through `clearAuth` also drops persisted search history.
        searchService.clearAllData();
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        impersonatingOrg: state.impersonatingOrg,
      }),
    }
  )
);

// Selector hooks
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);

// Permission checking hooks
export const useHasPermission = (permission: string) => {
  const user = useUser();
  // Admin users (super_admin, org_admin, principal) bypass the per-permission
  // list -- the backend's `User.has_permission` short-circuits on `is_admin`,
  // and we mirror that here so admin-flagged users actually see the actions
  // their server-side policy already permits. Without this, /me returns
  // is_superuser=true but no `permissions` array, so the UI hides every
  // gated action even though the API would accept the call.
  if (user?.is_superuser) return true;
  return user?.permissions?.includes(permission) ?? false;
};

export const useHasRole = (role: string) => {
  const user = useUser();
  return user?.roles?.includes(role) ?? false;
};

export const useCanAccessFeature = (_feature: keyof FeatureFlags) => {
  // This will be implemented when we have the organization store
  return true; // Placeholder
};
